// components/admin/LogsTab.js
// לוגי מערכת — CompanySettings Tab 4
//
// API (RESTful — per ANSWERS doc):
//   GET /debug/logs → { logs: [{filename, size, modified, type}], count }
//   GET /debug/logs/{filename}?lines=0 → { filename, content, lines_returned, total_lines }
//   GET /debug/status → { debug_mode: {enabled}, logs: {...}, logger: {enabled} }
//   POST /debug/enable → enable debug
//   POST /debug/disable → disable debug
//   POST /debug/clear → delete all logs
//
// UI:
//   - Log file list table (filename, size, modified, type, actions)
//   - Click "view" → modal with monospace content + syntax highlighting
//   - Syntax: [ERROR]=red, [WARNING]=orange, [INFO]=blue, [DEBUG]=gray
//   - Search/filter in log content (client-side)
//   - Filter by log level
//   - Toggle debug mode
//   - "Clear logs" button
//   - No download (security)

import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Chip,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '../../components/shared';
import { useApi, useApiMutation } from '../../hooks/useApi';
import api from '../../services/api';

// Log level color map
const LEVEL_COLORS = {
  ERROR: 'error.main',
  WARNING: 'warning.main',
  INFO: 'info.main',
  DEBUG: 'text.disabled',
};

// =============================================================================
// LogsTab
// =============================================================================
export default function LogsTab({ showSnackbar }) {
  const { t } = useTranslation();
  const { mutate, loading: mutating } = useApiMutation();

  // Log list
  const { data: logsData, loading, error, refetch } = useApi('/debug/logs', {
    defaultData: null,
    transform: (data) => data, // data = { logs: [...], count }
  });

  // Debug status
  const { data: debugStatus, refetch: refetchStatus } = useApi('/debug/status', {
    defaultData: null,
  });

  // Log viewer modal
  const [viewingLog, setViewingLog] = useState(null); // { filename, content, lines_returned, total_lines }
  const [logLoading, setLogLoading] = useState(false);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');

  // Clear confirm
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  const logs = logsData?.logs || [];
  const debugEnabled = debugStatus?.debug_mode?.enabled || false;

  // ===================================================================
  // View log content
  // ===================================================================
  const handleViewLog = useCallback(async (filename) => {
    if (logLoading) return;
    setLogLoading(true);
    setSearchText('');
    setLevelFilter('all');
    try {
      const response = await api.get(`/debug/logs/${encodeURIComponent(filename)}`, {
        params: { lines: 0 }, // 0 = all
      });
      const data = response.data?.data || response.data || {};
      setViewingLog(data);
    } catch (err) {
      showSnackbar(err?.response?.data?.message || t('common.error'), 'error');
    } finally {
      setLogLoading(false);
    }
  }, [showSnackbar, t]);

  // ===================================================================
  // Toggle debug mode
  // ===================================================================
  const handleToggleDebug = useCallback(async () => {
    if (mutating) return;
    const endpoint = debugEnabled ? '/debug/disable' : '/debug/enable';
    const result = await mutate('post', endpoint);
    if (result.success) {
      refetchStatus();
    } else {
      showSnackbar(result.error || t('common.error'), 'error');
    }
  }, [mutating, debugEnabled, mutate, refetchStatus, showSnackbar, t]);

  // ===================================================================
  // Clear logs
  // ===================================================================
  const handleClearLogs = useCallback(async () => {
    if (mutating) return;
    const result = await mutate('post', '/debug/clear');
    setClearConfirmOpen(false);
    if (result.success) {
      showSnackbar(t('companySettings.logs.clearSuccess'));
      refetch();
    } else {
      showSnackbar(result.error || t('common.error'), 'error');
    }
  }, [mutating, mutate, refetch, showSnackbar, t]);

  // ===================================================================
  // Parse & highlight log content
  // ===================================================================
  const filteredLines = useMemo(() => {
    if (!viewingLog?.content) return [];

    const lines = viewingLog.content.split('\n');

    return lines
      .map((line, i) => {
        // Detect level: [ERROR], [WARNING], [INFO], [DEBUG]
        const levelMatch = line.match(/\[(ERROR|WARNING|INFO|DEBUG)\]/i);
        const level = levelMatch ? levelMatch[1].toUpperCase() : null;
        return { text: line, level, index: i };
      })
      .filter((line) => {
        // Level filter
        if (levelFilter !== 'all' && line.level !== levelFilter.toUpperCase()) return false;
        // Text search
        if (searchText && !line.text.toLowerCase().includes(searchText.toLowerCase())) return false;
        return true;
      });
  }, [viewingLog, levelFilter, searchText]);

  // ===================================================================
  // Format helpers
  // ===================================================================
  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('he-IL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <Box data-testid="settings__logs-tab">
      {(loading || logLoading) && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControlLabel
          data-testid="settings__logs__switch--debug"
          control={
            <Switch
              checked={debugEnabled}
              onChange={handleToggleDebug}
              disabled={mutating}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BugReportIcon fontSize="small" />
              <Typography variant="body2">Debug Mode</Typography>
            </Box>
          }
        />

        <Box sx={{ flex: 1 }} />

        <Button
          data-testid="settings__logs__button--refresh"
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => { refetch(); refetchStatus(); }}
          disabled={loading}
        >
          {t('companySettings.logs.refreshLog')}
        </Button>

        <Button
          data-testid="settings__logs__button--clear"
          variant="outlined"
          size="small"
          color="error"
          startIcon={<DeleteSweepIcon />}
          onClick={() => setClearConfirmOpen(true)}
          disabled={mutating || logs.length === 0}
        >
          {t('companySettings.logs.clearOldLogs')}
        </Button>
      </Box>

      {/* Log files table */}
      {logs.length === 0 && !loading ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <BugReportIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {t('companySettings.logs.noLogs')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" data-testid="settings__logs__table">
            <TableHead>
              <TableRow>
                <TableCell>{t('companySettings.logs.logFile')}</TableCell>
                <TableCell>{t('companySettings.logs.logSize')}</TableCell>
                <TableCell>{t('companySettings.logs.logDate')}</TableCell>
                <TableCell sx={{ width: 80 }}>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.filename} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {log.filename}
                      </Typography>
                      <Chip
                        label={log.type}
                        size="small"
                        variant="outlined"
                        color={log.type === 'pre-login' ? 'primary' : 'default'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{formatSize(log.size)}</TableCell>
                  <TableCell>{formatDate(log.modified)}</TableCell>
                  <TableCell>
                    <Tooltip title={t('companySettings.logs.viewLog')}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewLog(log.filename)}
                        disabled={logLoading}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ============================================================= */}
      {/* Log Viewer Modal                                               */}
      {/* ============================================================= */}
      <Dialog
        open={!!viewingLog}
        onClose={() => setViewingLog(null)}
        maxWidth="lg"
        fullWidth
        data-testid="settings__logs__dialog--viewer"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {viewingLog?.filename}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {viewingLog && (
              <Chip
                label={`${filteredLines.length} / ${viewingLog.total_lines || '?'}`}
                size="small"
                variant="outlined"
              />
            )}
            <IconButton aria-label={t('common.close')} size="small" onClick={() => setViewingLog(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {/* Filters bar */}
          <Box sx={{ p: 1.5, display: 'flex', gap: 2, alignItems: 'center', borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
            <TextField
              data-testid="settings__logs__input--search"
              size="small"
              placeholder={t('companySettings.logs.searchInLog')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <ToggleButtonGroup
              data-testid="settings__logs__filter--level"
              value={levelFilter}
              exclusive
              onChange={(_, v) => v && setLevelFilter(v)}
              size="small"
            >
              {Object.entries(t('companySettings.logs.logLevels', { returnObjects: true })).map(([key, label]) => (
                <ToggleButton key={key} value={key} sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Log content */}
          <Box
            data-testid="settings__logs__content"
            sx={{
              p: 2,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              maxHeight: '60vh',
              overflow: 'auto',
              bgcolor: 'grey.50',
            }}
          >
            {filteredLines.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                {t('companySettings.logs.noLogs')}
              </Typography>
            ) : (
              filteredLines.map((line) => (
                <Box
                  key={line.index}
                  component="div"
                  sx={{
                    color: line.level ? LEVEL_COLORS[line.level] : 'text.primary',
                    fontWeight: line.level === 'ERROR' ? 600 : 400,
                    '&:hover': { bgcolor: 'action.hover' },
                    px: 0.5,
                    borderRadius: 0.5,
                  }}
                >
                  {line.text || '\u00A0'}
                </Box>
              ))
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1 }}>
          <Button onClick={() => setViewingLog(null)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================= */}
      {/* Clear Logs Confirm                                             */}
      {/* ============================================================= */}
      <ConfirmDialog
        open={clearConfirmOpen}
        title={t('companySettings.logs.clearOldLogs')}
        message={t('companySettings.logs.clearSuccess')}
        onConfirm={handleClearLogs}
        onCancel={() => setClearConfirmOpen(false)}
        confirmLabel={t('common.delete')}
        destructive
        loading={mutating}
      />
    </Box>
  );
}
